import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CSVLink } from 'react-csv';
import styled from 'styled-components';
import { InputLabel, Select } from '@components/Common';
import { ButtonColumn, FilterContainer } from '@components/Common/EntryFilters';
import DateFilterControlled from '@components/Common/EntryFilters/DateFilterControlled';
import ShipmentTypeFilterControlled from '@components/Common/EntryFilters/ShipmentTypeFilterControlled';
import TankSelectorControlled from '@components/Common/EntryFilters/TankSelectorControlled';
import NavBar from '@components/Pages/Reports/Common/ReportNavigation';
import { useTerminalsQuery } from '@api/queries/terminals';
import { useTanksQuery } from '@api/queries/tanks';
import { useCustomersQuery } from '@api/queries/customers';
import {
  useEntriesQuery,
  BookkeepingEntry,
  ActionDirection,
  ShipmentType,
} from '@api/queries/inventory';
import { useBilledEntryIdsQuery } from '@api/queries/billing';
import { useBillingReportFilters } from '@hooks/useBillingReportFilters';
import { compareDatesAsc } from '@helpers/dateUtils';
import BillingReportGrid from './BillingReportGrid';

const Columns = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;

  > div.column {
    box-sizing: border-box;
    width: 25%;
    padding-right: 15px;

    &.wide {
      flex-grow: 1;
    }

    &:last-child {
      padding-right: 0;
    }
  }
`;

const Column = styled.div`
  width: 200px;
`;

const BillingReport: React.FC = () => {
  const { t } = useTranslation();

  // URL-based filters
  const {
    filters,
    tankFilter,
    dateFilter,
    setTankFilter,
    setCustomerId,
    setDateFilter,
    setShipmentType,
  } = useBillingReportFilters();

  // Fetch reference data using TanStack Query
  const { data: terminals = [] } = useTerminalsQuery();
  const { data: tanks = [] } = useTanksQuery();
  const { data: customers = [] } = useCustomersQuery();

  // Customer options for select
  const customerOptions = useMemo(
    () => customers.map((c) => ({ value: c.id, label: c.name })),
    [customers]
  );

  const selectedCustomerOption = useMemo(
    () => customerOptions.find((c) => c.value === filters.customerId),
    [customerOptions, filters.customerId]
  );

  const customerChanged = useCallback(
    (newCustomerId: string | null) => {
      setCustomerId(newCustomerId);
    },
    [setCustomerId]
  );

  // Fetch entries for the selected terminal
  const { data: allEntries = [] } = useEntriesQuery(
    { terminalId: filters.terminalId, from: filters.startDate },
    !!filters.terminalId || !!filters.startDate
  );

  // Fetch billed entry IDs
  const { data: billedEntryIds = new Set<string>() } = useBilledEntryIdsQuery();

  // Filter entries based on current filters
  const filteredEntries = useMemo(() => {
    return allEntries.filter((entry: BookkeepingEntry) => {
      const { terminalId, tankId, customerId, startDate, endDate, shipmentType } = filters;

      return (
        (!customerId || entry.customerId === customerId) &&
        (!tankId || entry.tankId === tankId) &&
        (!terminalId || entry.terminalId === terminalId) &&
        (shipmentType === null || entry.shipmentType === shipmentType) &&
        (!startDate || compareDatesAsc(entry.order, startDate) >= 0) &&
        (!endDate || compareDatesAsc(entry.order, endDate) <= 0)
      );
    });
  }, [allEntries, filters]);

  const csvHeaders = [
    { label: t('entry.terminal'), key: 'terminalName' },
    { label: t('entry.tank'), key: 'tankName' },
    { label: t('entry.customer'), key: 'customerName' },
    { label: t('entry.product'), key: 'productName' },
    { label: t('entry.direction'), key: 'actionDirection' },
    { label: t('reports.carrier'), key: 'shipmentTypeName' },
    { label: t('reports.carrier_information'), key: 'carrierInformation' },
    { label: t('entry.amount'), key: 'amount' },
    { label: t('entry.notes'), key: 'notes' },
    { label: t('entry.billing'), key: 'billingInformation' },
    { label: t('entry.isBilled'), key: 'isBilled' },
    { label: t('entry.unloading_finished'), key: 'unloadingFinished' },
  ];

  const totalWeight =
    filteredEntries.reduce(
      (total, c) => total + c.amount * (c.actionDirection === ActionDirection.Incoming ? 1 : -1),
      0
    ) / 1000;

  return (
    <>
      <NavBar />
      <FilterContainer>
        <Columns>
          <TankSelectorControlled
            value={tankFilter}
            onChange={setTankFilter}
            terminals={terminals}
            tanks={tanks}
          />
          <Column className="column">
            <InputLabel htmlFor="customerId">{t('entry.customer')}</InputLabel>
            <Select
              name="customerId"
              placeholder={t('entry.select_customer')}
              value={selectedCustomerOption ? selectedCustomerOption : { value: '', label: '' }}
              onChange={(i: { value: string } | null, j: { action: string }) =>
                customerChanged(j.action === 'clear' ? null : i?.value ?? null)
              }
              options={customerOptions}
            />
          </Column>
          <DateFilterControlled value={dateFilter} onChange={setDateFilter} />
          <ShipmentTypeFilterControlled value={filters.shipmentType} onChange={setShipmentType} />
          <ButtonColumn className="column">
            <CSVLink data={filteredEntries} headers={csvHeaders}>
              {t('reports.export_to_pdf')}
            </CSVLink>
          </ButtonColumn>
        </Columns>
      </FilterContainer>

      <Columns>
        <h1>
          <InputLabel>
            {t('reports.billing_entry_sum')} {totalWeight} {t('reports.billing_ton')}
            {t('reports.billing_entry_count')} {filteredEntries.length}
          </InputLabel>
        </h1>
      </Columns>
      <BillingReportGrid entries={filteredEntries} billedEntryIds={billedEntryIds} />
    </>
  );
};

export default BillingReport;
